import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { listFindings } from '../api/findings';
import { listAudit } from '../api/audit';
import { listTriageSessions } from '../api/triage';
import { listRuns } from '../api/runs';
import { KpiTile, type KpiTileProps } from '../components/KpiTile';
import { formatDate, relativeTime } from '../lib/format';

const STALE = 30_000;

function useKpiData() {
  const findings = useQuery({
    queryKey: ['findings', { status: 'open', limit: 10 }],
    queryFn: () => listFindings({ status: 'open', limit: 10 }),
    staleTime: STALE,
    refetchInterval: STALE,
  });
  const runs = useQuery({
    queryKey: ['runs', { limit: 50 }],
    queryFn: () => listRuns({ limit: 50 }),
    staleTime: STALE,
    refetchInterval: STALE,
  });
  const sessions = useQuery({
    queryKey: ['triage-sessions', { status: 'in_progress' }],
    queryFn: () => listTriageSessions({ status: 'in_progress', limit: 10 }),
    staleTime: STALE,
    refetchInterval: STALE,
  });
  const today = new Date().toISOString().slice(0, 10);
  const audit = useQuery({
    queryKey: ['audit', { date: today }],
    queryFn: () => listAudit({ date: today }),
    staleTime: STALE,
    refetchInterval: STALE,
  });
  return { findings, runs, sessions, audit };
}

/**
 * Statuses that count as a worker OUTCOME and therefore belong in the
 * success-rate denominator. From the RunStatus enum
 * (`packages/shared/src/schemas/run.ts`):
 *
 *   IN:
 *     - `done`            — worker finished its candidate loop
 *     - `needs_attention` — worker bailed but completed the pass
 *     - `failed`          — worker crashed; a failed outcome IS an outcome,
 *                           so a window of 5 failures + 1 applying done
 *                           should read ~17%, not 100% (issue #112).
 *
 *   OUT:
 *     - `cancelled`        — user clicked Stop; not a worker outcome,
 *                            skews the rate if a user habitually cancels.
 *     - `paused_budget`    — worker halted on LLM budget cap; the worker
 *                            did its job, neither a success nor a failure.
 *     - `time_cap_reached` — worker halted on per-tier wall-clock cap
 *                            (#379); same posture as `paused_budget` —
 *                            the worker did its job within the window
 *                            allowed by the user's tier.
 *     - `queued`           — pre-flight, no outcome yet.
 *     - `running` /
 *       `starting`      — in flight; partial counters are not an outcome
 *                         and would deflate the rate while in progress.
 */
const TERMINAL_STATUSES = new Set(['done', 'needs_attention', 'failed']);

/**
 * Success rate over runs in the dashboard's 24h window.
 *
 * Definition: a run is "successful" iff it reached a terminal state AND
 * the worker actually applied to at least one job. The denominator is
 * restricted to terminal outcomes (see `TERMINAL_STATUSES` above for the
 * in/out judgment per status and why).
 *
 * Why count is gated by `counters.jobsApplied > 0`, not just terminal
 * status: a run can finish cleanly (`status: 'done'`) with zero applies
 * because every candidate hit an unknown-ATS dead-end or got filtered
 * out. Counting that as a success produced the "100% with zero applies"
 * anomaly in issue #109.
 */
export function computeSuccessRate(
  items: { status: string; counters?: { jobsApplied?: number } }[],
): string {
  const terminal = items.filter((r) => TERMINAL_STATUSES.has(r.status));
  if (!terminal.length) return '—';
  const succeeded = terminal.filter((r) => (r.counters?.jobsApplied ?? 0) > 0).length;
  return `${Math.round((succeeded / terminal.length) * 100)}%`;
}

function isActiveRun(r: { status: string }): boolean {
  return r.status === 'running' || r.status === 'starting';
}

/**
 * Restricts a list of runs to those started in the last 24 hours.
 *
 * The dashboard's "Run Success Rate (24h)" tile (#114) was previously
 * computing over the entire fetched runs list (50 most recent) — the
 * label promised a 24h window the data didn't honor. This helper makes
 * the window enforcement explicit, testable, and decoupled from
 * `Date.now()` via the `now` parameter.
 *
 * Boundary: cutoff is strict (`>`), so a run with `startedAt === now-24h`
 * is excluded. A run 1ms newer is included.
 */
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

export function filterRunsToLast24h<T extends { startedAt: string }>(
  items: T[],
  now: number,
): T[] {
  const cutoff = now - ONE_DAY_MS;
  return items.filter((r) => Date.parse(r.startedAt) > cutoff);
}

function prepareDashboardData(kpi: ReturnType<typeof useKpiData>) {
  const openFindings = kpi.findings.data?.items ?? [];
  const runsItems = kpi.runs.data?.items ?? [];
  const inProgress = kpi.sessions.data?.items ?? [];
  const runsLast24h = filterRunsToLast24h(runsItems, Date.now());
  return {
    openFindings,
    activeRuns: runsItems.filter(isActiveRun),
    successRate: computeSuccessRate(runsLast24h),
    inProgress,
    auditItems: kpi.audit.data?.items ?? [],
    findingsAccent: (openFindings.length > 0 ? 'warning' : 'success') as KpiTileProps['accent'],
    sessionsAccent: (inProgress.length > 0 ? 'warning' : 'default') as KpiTileProps['accent'],
  };
}

interface OpenFindingsListProps {
  isLoading: boolean;
  items: Array<{ findingId: string; category: string; lastSeenAt: string; summary: string; count: number; affectedUserIds: string[] }>;
}

export function OpenFindingsList({ isLoading, items }: OpenFindingsListProps): React.ReactElement {
  return (
    <section>
      <h2 className="text-sm font-semibold text-text-secondary mb-3">Open Findings</h2>
      {isLoading && <p className="text-sm text-text-muted">Loading…</p>}
      {!isLoading && items.length === 0 && (
        <p className="text-sm text-text-muted">No open findings.</p>
      )}
      <ul role="list" className="space-y-2">
        {items.map((f) => (
          <li key={f.findingId} className="bg-white border border-border rounded-md p-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium px-2 py-0.5 bg-orange-100 text-orange-800 rounded-full">
                {f.category}
              </span>
              <span className="text-xs text-text-muted">{relativeTime(f.lastSeenAt)}</span>
            </div>
            <p className="mt-1 text-sm text-text-primary">{f.summary}</p>
            <p className="text-xs text-text-muted mt-0.5">
              {f.count} occurrences · {f.affectedUserIds.length} users
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}

interface RecentActivityProps {
  isLoading: boolean;
  items: Array<{ actionId: string; createdAt: string; actorEmail: string; action: string }>;
}

export function RecentActivity({ isLoading, items }: RecentActivityProps): React.ReactElement {
  return (
    <section>
      <h2 className="text-sm font-semibold text-text-secondary mb-3">Recent Admin Activity</h2>
      {isLoading && <p className="text-sm text-text-muted">Loading…</p>}
      <ul role="list" className="space-y-1">
        {items.slice(0, 8).map((a) => (
          <li key={a.actionId} className="flex items-start gap-2 text-sm">
            <span className="text-text-muted shrink-0">{formatDate(a.createdAt)}</span>
            <span className="text-text-secondary">
              {a.actorEmail} — {a.action}
            </span>
          </li>
        ))}
        {!isLoading && items.length === 0 && (
          <li className="text-sm text-text-muted">No activity today.</li>
        )}
      </ul>
    </section>
  );
}

interface ActiveSessionsProps {
  isLoading: boolean;
  items: Array<{ sessionId: string; actorEmail: string; startedAt: string; findingsExamined: string[]; prsOpened: unknown[] }>;
}

export function ActiveSessions({ isLoading, items }: ActiveSessionsProps): React.ReactElement {
  return (
    <section>
      <h2 className="text-sm font-semibold text-text-secondary mb-3">Active Triage Sessions</h2>
      {isLoading && <p className="text-sm text-text-muted">Loading…</p>}
      <ul role="list" className="space-y-2">
        {items.map((s) => (
          <li key={s.sessionId} className="bg-white border border-border rounded-md p-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-text-secondary">{s.actorEmail}</span>
              <span className="text-xs text-text-muted">{relativeTime(s.startedAt)}</span>
            </div>
            <p className="text-xs text-text-muted mt-0.5">
              {s.findingsExamined.length} findings · {s.prsOpened.length} PRs
            </p>
          </li>
        ))}
        {!isLoading && items.length === 0 && (
          <li className="text-sm text-text-muted">No active sessions.</li>
        )}
      </ul>
    </section>
  );
}

export function Dashboard(): React.ReactElement {
  const kpi = useKpiData();
  const d = prepareDashboardData(kpi);

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold text-text-primary">Dashboard</h1>

      <div className="grid grid-cols-4 gap-4">
        <KpiTile label="Open Findings" value={d.openFindings.length} accent={d.findingsAccent} />
        <KpiTile label="Active Runs" value={d.activeRuns.length} accent="default" />
        <KpiTile label="Run Success Rate (24h)" value={d.successRate} accent="success" />
        <KpiTile label="Triage Sessions" value={d.inProgress.length} sub="in progress" accent={d.sessionsAccent} />
      </div>

      <div className="grid grid-cols-2 gap-6">
        <OpenFindingsList isLoading={kpi.findings.isLoading} items={d.openFindings} />
        <div className="space-y-6">
          <RecentActivity isLoading={kpi.audit.isLoading} items={d.auditItems} />
          <ActiveSessions isLoading={kpi.sessions.isLoading} items={d.inProgress} />
        </div>
      </div>
    </div>
  );
}
